"use client";

import * as React from "react";
import { cn } from "../utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";
import { ProgressRing } from "./progress-ring";

export interface ConfidenceIndicatorProps {
  /** Confidence value from 0 to 1 (e.g. 0.85 = 85%) */
  value: number;
  /** Size of the indicator */
  size?: "sm" | "md" | "lg";
  /** Additional class name */
  className?: string;
}

// ============ Shared color helpers ============

/**
 * Returns a Tailwind background class based on confidence percentage.
 * Red (0-25%) → Orange (25-50%) → Yellow (50-75%) → Green (75-100%)
 */
function getConfidenceBackground(value: number): string {
  const percent = Math.round(value * 100);
  if (percent >= 75) return "bg-green-500 dark:bg-green-400";
  if (percent >= 50) return "bg-yellow-500 dark:bg-yellow-400";
  if (percent >= 25) return "bg-orange-500 dark:bg-orange-400";
  return "bg-red-500 dark:bg-red-400";
}

/** Returns a raw hex color for SVG/CSS usage. */
function getConfidenceColor(value: number): string {
  const percent = Math.round(value * 100);
  if (percent >= 75) return "#22c55e";
  if (percent >= 50) return "#eab308";
  if (percent >= 25) return "#f97316";
  return "#ef4444";
}

/** Clamp a value to the 0..1 range and return [clamped, percent]. */
function clampConfidence(value: number): [clamped: number, percent: number] {
  const clamped = Math.min(1, Math.max(0, value));
  return [clamped, Math.round(clamped * 100)];
}

const dotSizeClasses = {
  sm: "w-2 h-2",
  md: "w-2.5 h-2.5",
  lg: "w-3 h-3",
} as const;

const svgSizes = { sm: 10, md: 12, lg: 16 } as const;

// ============ Variant: Dot (original) ============

/**
 * ConfidenceIndicator - A colored dot that represents a confidence level.
 *
 * Color scale: red (0-25%) → orange (25-50%) → yellow (50-75%) → green (75-100%)
 * Shows the exact percentage in a tooltip on hover.
 */
export function ConfidenceIndicator({ value, size = "md", className }: ConfidenceIndicatorProps) {
  const [, percent] = clampConfidence(value);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`${percent}% confidence`}
          className={cn(
            dotSizeClasses[size],
            "inline-block rounded-full shrink-0",
            getConfidenceBackground(value),
            className,
          )}
        />
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{percent}% confidence</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ============ Variant: Pie ============

/**
 * ConfidenceIndicatorPie - A mini pie chart filled proportionally to the value.
 *
 * Uses conic-gradient to draw a filled wedge. The filled portion uses the
 * confidence color scale; the unfilled portion is a light gray track.
 */
export function ConfidenceIndicatorPie({
  value,
  size = "md",
  className,
}: ConfidenceIndicatorProps) {
  const [clamped, percent] = clampConfidence(value);
  const color = getConfidenceColor(clamped);
  const px = svgSizes[size];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`${percent}% confidence`}
          className={cn("inline-block rounded-full shrink-0", className)}
          style={{
            width: px,
            height: px,
            background: `conic-gradient(${color} 0% ${percent}%, #e5e7eb ${percent}% 100%)`,
          }}
        />
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{percent}% confidence</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ============ Variant: Ring ============

/**
 * ConfidenceIndicatorRing - A donut/ring arc that fills proportionally.
 *
 * SVG circle with stroke-dasharray to draw a partial arc. Shows a gray
 * background track with the confidence-colored arc on top.
 */
const strokeWidths = { sm: 2, md: 2.5, lg: 3 } as const;

export function ConfidenceIndicatorRing({
  value,
  size = "md",
  className,
}: ConfidenceIndicatorProps) {
  const [clamped, percent] = clampConfidence(value);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`${percent}% confidence`}
          className={cn("inline-block shrink-0", className)}
        >
          <ProgressRing
            value={clamped}
            size={svgSizes[size]}
            strokeWidth={strokeWidths[size]}
            linecap="round"
            trackStyle={{ stroke: "#e5e7eb" }}
            arcStyle={{ stroke: getConfidenceColor(clamped) }}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{percent}% confidence</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ============ Variant: Bar ============

/**
 * ConfidenceIndicatorBar - A tiny horizontal progress bar.
 *
 * Shows a gray track with a colored fill proportional to the value.
 */
const barHeights = { sm: "h-1", md: "h-1.5", lg: "h-2" } as const;
const barWidths = { sm: "w-6", md: "w-8", lg: "w-10" } as const;

export function ConfidenceIndicatorBar({
  value,
  size = "md",
  className,
}: ConfidenceIndicatorProps) {
  const [clamped, percent] = clampConfidence(value);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`${percent}% confidence`}
          className={cn(
            "inline-block shrink-0 rounded-full bg-stone-200 dark:bg-stone-700 overflow-hidden",
            barWidths[size],
            barHeights[size],
            className,
          )}
        >
          <span
            className={cn("block h-full rounded-full", getConfidenceBackground(clamped))}
            style={{ width: `${percent}%` }}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{percent}% confidence</p>
      </TooltipContent>
    </Tooltip>
  );
}
